import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Edit, Eye, EyeOff, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import PageHeader from '../../components/admin/PageHeader';
import DataTable from '../../components/admin/DataTable';
import LoadingState from '../../components/admin/LoadingState';

interface LegalPageData {
  id: string;
  slug: string;
  title: string;
  content: string;
  meta_description: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

const LegalPages: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<LegalPageData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('legal_pages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPages(data || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Erreur lors du chargement des pages');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublished = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('legal_pages')
        .update({ published: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast.success(currentStatus ? 'Page dépubliée' : 'Page publiée');
      fetchPages();
    } catch (error) {
      console.error('Error toggling published status:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Titre',
      render: (page: LegalPageData) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <FileText size={20} className="text-primary-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{page.title}</div>
            <div className="text-sm text-gray-500">/{page.slug}</div>
          </div>
        </div>
      )
    },
    {
      key: 'published',
      label: 'Statut',
      render: (page: LegalPageData) => (
        <button
          onClick={() => handleTogglePublished(page.id, page.published)}
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            page.published
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {page.published ? <Eye size={14} /> : <EyeOff size={14} />}
          {page.published ? 'Publié' : 'Brouillon'}
        </button>
      )
    },
    {
      key: 'updated_at',
      label: 'Dernière modification',
      render: (page: LegalPageData) => (
        <div className="text-sm text-gray-600">
          {new Date(page.updated_at).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (page: LegalPageData) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/admin/legal-pages/${page.id}`)}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Modifier"
          >
            <Edit size={18} />
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Pages Légales"
          description="Gérez les mentions légales, politique de confidentialité et CGU"
          icon={<FileText className="w-8 h-8 text-primary-600" />}
        />
        <LoadingState />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Pages Légales"
        description="Gérez les mentions légales, politique de confidentialité et CGU"
        icon={<FileText className="w-8 h-8 text-primary-600" />}
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <DataTable
          columns={columns}
          data={pages}
          emptyMessage="Aucune page légale trouvée"
        />
      </div>
    </div>
  );
};

export default LegalPages;
